import { Box, Divider, Stack, Typography } from "@mui/material";
import { DownloadAllCutoffAdvisorOverviewsButton } from "Components/Settings/Invoices/DownloadAllCutoffAdvisorOverviewsButton";
import { DownloadAccountantDescriptionButton } from "Components/Settings/Invoices/DownloadAccountantDescriptionButton";
import { BlDefaultTooltip } from "Components/Shared/BlTooltipDefault";
import { BlDefaultButton } from "Components/Shared/Buttons/BlDefaultButton";
import { BlDataTable } from "Components/Shared/DataTable/BlDataTable";
import { ContractIcon } from "Components/Shared/Icons";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { useAppDispatch } from "Hooks/State/useAppDispatch";
import { getCutoffAdvisorOverviewsAsync } from "State/Cutoff/GetCutoffAdvisorOverviewsSlice";
import { getCutoffAdvisorOverviewPdfAsync } from "State/Cutoff/GetCutoffAdvisorOverviewPdf";
import { useAppSelector } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { useEffect, useState } from "react";
import { styled } from "styled-components";

const StyledValue = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const CutoffAdvisorOverviewsList: React.FunctionComponent = _ => {
  const { t } = useResource();
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState({
    limit: 25,
    offset: 0,
  });

  useEffect(() => {
    dispatch(
      getCutoffAdvisorOverviewsAsync.request({
        ...filter,
      }),
    );
  }, [dispatch, filter]);

  const onPageChange = (page: number, rows: number) => {
    setFilter({ ...filter, offset: page * rows, limit: rows });
  };

  const { data, isLoading } = useAppSelector(
    state => state.cutoff.advisorOverviews,
  );
  const pdfState = useAppSelector(state => state.cutoff.advisorOverviewPdf);

  const formatPeriodDate = (dateFrom: string, dateTo: string) =>
    `${formatDate(new Date(dateFrom))} - ${formatDate(new Date(dateTo))}`;

  // Skrýt celou sekci pokud nejsou žádné přehledy
  if (!data || data.totalCount === 0) {
    return null;
  }

  return (
    <>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Box maxWidth={"80%"}>
          <StyledFlex $gap={0.5} $alignItems="center">
            <Typography variant="h2">
              {t(Resources.Settings.Cutoff.AdvisorOverview.Title)}
            </Typography>

            <BlDefaultTooltip
              title={t(Resources.Settings.Cutoff.AdvisorOverview.Tooltip)}
            />
          </StyledFlex>

          <Typography variant="subtitle2" fontStyle={"normal"}>
            {t(Resources.Settings.Cutoff.AdvisorOverview.Description)}
          </Typography>
        </Box>

        <Box display={"flex"} alignItems={"flex-end"} gap={2}>
          <DownloadAccountantDescriptionButton />
          <DownloadAllCutoffAdvisorOverviewsButton isDisabled={!data?.totalCount} />
        </Box>
      </Stack>

      <Divider sx={{ marginTop: 3, marginBottom: 2 }} />

      <BlDataTable
        isLoading={isLoading}
        totalCount={data?.totalCount}
        rows={data?.items.map(e => ({ data: e })) ?? []}
        rowEntityID="publicID"
        getHeaderCell={column => {
          return <div>{column.label ?? column.id}</div>;
        }}
        onPageChange={onPageChange}
        skeletonRowsCount={5}
        columns={[
          {
            id: "dateFrom",
            label: t(
              Resources.Settings.Cutoff.AdvisorOverview.Period,
            ),
          },
          {
            id: "companyName",
            label: t(
              Resources.Settings.Cutoff.AdvisorOverview
                .CompanyName,
            ),
          },
          { id: "action", label: "", maxWidth: 0, justifySelf: "flex-end" },
        ]}
        getCell={(column, row) => {
          switch (column) {
            case "dateFrom":
              return (
                <Typography fontWeight={600}>
                  {formatPeriodDate(row.dateFrom, row.dateTo)}
                </Typography>
              );

            case "companyName":
              return <StyledValue>{row.companyName}</StyledValue>;

            case "action":
              return (
                <StyledFlex $alignItems={"center"} $gap={2.5}>
                  <BlDefaultButton
                    title={t(
                      Resources.Settings.Cutoff.AdvisorOverview
                        .DownloadPdf,
                    )}
                    isLoading={
                      pdfState.isLoading &&
                      pdfState.cutoffPublicID === row.publicID
                    }
                    onClick={() =>
                      dispatch(
                        getCutoffAdvisorOverviewPdfAsync.request({
                          cutoffPublicID: row.publicID,
                          period: formatPeriodDate(row.dateFrom, row.dateTo),
                        }),
                      )
                    }
                  >
                    <ContractIcon />
                  </BlDefaultButton>

                  <div></div>
                </StyledFlex>
              );

            default:
              const value = (row as any)[column as any] ?? "";

              return <div>{value}</div>;
          }
        }}
      />
    </>
  );
};
